<template>
    <div>
        <!-- Avatar -->
        <div class="row mt-4 d-none d-xl-block">
            <div class="col-md-6 offset-md-3">
                <div class="avatar">
                    <img :src="avatar" height="140" class="mx-auto d-block sau-avatar bdr-blue md" />
                </div>
            </div>
        </div>

        <!-- Form -->
        <div class="row mt-6" v-if="checked">
            <div class="col">
                <main class="dark-wrapper">
                    <form>
                        <!-- Blank field start-->
                        <div class="d-none" id="blank-id-wrapper">
                            <label>Keep this field blank</label>
                            <input type="text" name="blank-id" id="blank-id" />
                        </div>
                        <!-- Blank field end -->

                        <!-- Verification Code -->
                        <div class="form-row">
                            <div class="col">
                                <h3 class="text-center text-secondary">Two Factor Authentication</h3>
                            </div>
                        </div>
                        <template v-if="expired && !loading">
                            <div class="form-row">
                                <div class="col">
                                    <div>
                                        <p>Your verification attempt has expired. Please log in again to continue.</p>
                                        <button class="btn btn-block btn-lg btn-light" @click.prevent="returnToLogin">Return to Login</button>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="form-row">
                                <div class="col">
                                    <div class="my-2 text-center text-secondary">
                                        <span v-if="method !== 'authenticator'">You have chosen <span class="sau-font-bold">{{method}}</span> as your authentication method.</span>
                                        <span v-else>Open your Authenticator App and enter the code for MySAU.</span>
                                    </div>
                                    <div class="form-group" v-if="didSendCode">
                                        <label class="sr-only" for="email">Verification Code</label>
                                        <div class="input-group">
                                            <input v-model="token" id="code" name="token" class="form-control form-control-lg p-4" placeholder="Enter verification code" dusk="two-factor-code" autocomplete="off" required autofocus />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="row my-3">
                                 <div class="col text-center">
                                    <b-form-checkbox id="rememberme"
                                            v-model="trustBrowser"
                                            >
                                        Trust this browser for 30 Days
                                        <span class="sau-help sm align-middle" v-b-tooltip.hover :title="tooltip">?</span>
                                    </b-form-checkbox>
                                </div>
                            </div>
                            <!-- Button -->
                            <div class="form-row">
                                <div class="col">
                                    <button type="submit"
                                            :disabled="shouldDisable"
                                            @click.prevent="didClick($event)"
                                            dusk="two-factor-button"
                                            class="btn btn-block btn-lg"
                                            :class="!this.token && this.didSendCode ? 'btn-light':'btn-success'">{{buttonLabel}}
                                            <i v-if="loading" class="far fa-spinner-third fa-spin"></i>
                                    </button>
                                    <div class="mt-2 text-center text-danger">
                                        <span v-if="error">{{ error }}</span>
                                    </div>
                                </div>
                            </div>
    
                            <template v-if="alternateMethods.length > 0">
                                <div class="mt-3">
                                    <template v-for="(method, index) in alternateMethodData">
                                        <template v-if="alternateMethods.includes(method.name)">
                                            <div
                                                class="flex flex-row items-center mb-6 hover:text-primary cursor-pointer"
                                                :key="index"
                                                :tabindex="index"
                                                aria-pressed="false"
                                                role="button"
                                                @click.prevent="selectedAlternateMethod = method.name">
                                                    <div class="w-1/6 text-center">
                                                        <i class="text-2xl" :class="method.icon"></i>
                                                    </div>
                                                    <div class="w-5/6 text-sm">
                                                        <p class="text-sm mb-0">{{ method.description }}</p>
                                                    </div>
                                            </div>
                                        </template>
                                    </template>
                                </div>
                            </template>
                            <template v-else>
                                <div class="alert alert-warning mt-3 mb-0" role="alert">
                                    <p class="mb-0 text-xs">You have only one two-factor authentication method on your account. We recommend enabling additional methods to avoid losing access.</p>
                                </div>
                            </template>
                        </template>
                    </form>
                </main>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: ['route','method', 'alternate', 'all'],
    data() {
        return {
            expired: false,
            buttonLabel: 'Send code to '+ this.method,
            didSendCode: false,
            googleAuthenticator: false,
            token: '',
            loading: false,
            avatar: '/img/avatar.svg',
            error: '',
            alternateMethods: [],
            allMethods: [],
            selectedAlternateMethod:'',
            checked: false,
            tooltip: 'Trusted browsers will not require two factor authentication',
            ip: '',
            trustBrowser: false,
            displayOtherVerificationMethods: false,
            alternateMethodData: [
                {
                    name: 'authenticator',
                    description: 'Use a verification code from my authenticator app',
                    icon: 'fal fa-shield-check',
                },
                {
                    name: 'email',
                    description: 'Send a verification code to my email',
                    icon: 'fal fa-envelope',
                },
                {
                    name: 'mobile',
                    description: 'Send a verification code to my phone',
                    icon: 'fal fa-mobile',
                },
            ],
        }
    },
    created(){
        this.alternateMethods = JSON.parse(this.alternate);
        // all two factor methods
        this.allMethods = JSON.parse(this.all);
        // Check the method in the url has been setup by the user - prevents users manually entering a method which they don't support
        let selectedMethod = this.getTwoFactorMethod();
        if(this.allMethods.indexOf(selectedMethod) == -1) {
            // Selected method doesn't exist, user is trying to be tricky and modifying the url ;) - redirect that fool!
            window.location = window.location.href.replace(selectedMethod, this.allMethods[0]);
        }

        // if no route, we are using authenticator and don't need to request a code
        if(window.location.href.indexOf('authenticator') > -1){
            // authenticator doesn't require this so set state to accept code
            this.didSendCode = true
            this.googleAuthenticator = true;
            this.buttonLabel = 'Verify';
        }
        this.checked = true;
    },
    watch:{
        selectedAlternateMethod: function(selectedAlternateMethod) {
            var url = window.location.toString().replace(this.method,selectedAlternateMethod);
            window.location.replace(url)
        }
    },
    methods:{
        didClick(){
            this.error = '';
            this.didSendCode ? this.attemptVerification() : this.requestVerificationCode()
        },
        requestVerificationCode(){
            this.loading = true;
            this.buttonLabel = 'Sending Code'
            axios.post('/login/two-factor/'+this.method+'/'+this.route+'/request')
                .then((response) => {
                    this.buttonLabel = 'Verify Code'
                    this.didSendCode = true
                    this.loading = false
                })
                .catch((error) => {
                    this.loading = false;
                    this.buttonLabel = 'Send Code';
                    this.error = this.getErrorMessage(error);
                });
        },
        attemptVerification(){
            if(!this.token){
                return this.error = 'Please enter your verification code'
            }

            var url = this.googleAuthenticator ?
                '/login/two-factor/authenticator/'+this.route :
                '/login/two-factor/'+this.route+'/response';

            this.loading = true;
            this.buttonLabel = 'Verifying Code'
            axios.defaults.withCredentials = true
            axios.post(url, {token: this.token, trust_browser: this.trustBrowser})
                .then((response) => {
                    this.buttonLabel = 'Redirecting';
                    window.location.replace(response.data.redirect);
                })
                .catch((error) => {
                    this.loading = false;
                    this.buttonLabel = 'Verify';
                    this.error = 'Invalid verification code. Please try again. ';
                });
        },
        expire(data) {
            axios.post('/log-client-error', {
                data: {
                    ...data,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    browserTime: new Date().toString(),
                    browser: navigator.userAgent,
                    token: this.token,
                    trustBrowser: this.trustBrowser,
                    route: this.route,
                    codeSent: this.didSendCode,
                    error: this.error,
                },
                message: 'Two factor authentication expired',
                code: 'two-factor-expired'
            })
            .catch(() => {});

            this.expired = true;
        },
        returnToLogin() {
            window.location.href = '/login';
        },
        getErrorMessage(error) {
            if (error.response && error.response.data && error.response.data.message) {
                return error.response.data.message;
            }

            return 'There was a problem with your request. Please contact support.';
        },
    },
    computed: {
        shouldDisable() {
            return (!this.token && this.didSendCode) || this.loading;
        }
    }
}
</script>
<style>
</style>
